import { gsap } from 'gsap';
import type { Actor, AttackSkill } from '../../../battle/types';
import type { SkillBundle } from '../../types';
import { travelGroundCrack } from './GroundCrack';

const ROOT = '/assets/textures/characters/sparky_bot/skills/earthshake';

// Sheet layout. Same 4x4 convention as the other skill / character sheets.
const SHEET_COLS = 4;
const SHEET_ROWS = 4;
// Defaults - overridable per character via timings.skills.earthshake.
// IMPACT_FRAME: frame where the slam lands (feet hit ground); screen shake and
// arena ball-drops fire there so they sync with the visible impact.
// ANIM_SPEED: sheet playback speed (frames/tick, further damped by ticker).
const IMPACT_FRAME = 8;
const ANIM_SPEED = 1;

// Scene-wide shake fired on the slam. Amplitude in design-px; decays per cycle.
const SHAKE_CYCLES = 4;
const SHAKE_AMPLITUDE = 3.5;
const SHAKE_CYCLE_DURATION = 0.05;

// Ground shockwave crack that tears from the attacker toward the opponent. We
// AWAIT its travel before returning so the engine's damage / painAir lands the
// instant the crack reaches the defender. Duration is in BATTLE-pace seconds.
const CRACK_TRAVEL_DURATION = 0.16;
// Crack starts just in front of the attacker's feet (toward the opponent) so it
// reads as spreading out from the anvil impact, not from inside the body.
const CRACK_START_INSET = 30;

const ENGINE: AttackSkill = {
  kind: 'attack',
  id: 'earthshake',
  name: 'Earthshake',
  // Tuned low: this skill's value is releasing embedded arena objects, not the
  // direct damage. damageMultiplier 0.5 makes the jump itself a light tap.
  triggerChance: 0.3,
  damageMultiplier: 0.5,
  pierceBlock: false,
  animationId: 'earthshake',
  effects: ['earthshake'],
  launches: true, // ground slam pops the defender off their feet (pain-air)
};

export const EARTHSHAKE: SkillBundle = {
  engine: ENGINE,
  assets: {
    sheets: {
      earthshake: { url: `${ROOT}/earthshake.png`, cols: SHEET_COLS, rows: SHEET_ROWS },
    },
    textures: {
      logIcon: `${ROOT}/ui-log-icon.png`,
    },
  },
  async playAttack(ctx) {
    const { attacker, defender, scene, assets } = ctx;
    const frames = assets.sheets.earthshake;
    if (!frames) return;

    // Per-character overrides (speed / offset / impact frame) fall back to the
    // skill's built-in defaults.
    const t = attacker.timings.skills?.[ENGINE.id];
    const impactFrame = t?.impactFrame ?? IMPACT_FRAME;

    // The sheet draws the whole jump+slam in place - no GSAP body tween needed.
    // Shadow (a sibling of body) stays planted automatically. Recovery frames
    // keep playing after we return; schedule the idle snap so the attacker
    // doesn't freeze on the last frame.
    attacker.playFrames(frames, {
      loop: false,
      speed: t?.speed ?? ANIM_SPEED,
      offsetX: t?.offsetX,
      offsetY: t?.offsetY,
      scaleMultiplier: t?.scale,
    });
    attacker.scheduleIdleAfterCurrentAnim();

    // Wait for the slam frame, then fire shake + arena releases fire-and-forget
    // so the playAttack hook returns at impact and subsequent damage events
    // render ON TOP of the shake / falling balls instead of after them.
    await attacker.awaitFrame(impactFrame);

    // Screen shake.
    const tl = gsap.timeline({
      onComplete: () => {
        scene.x = 0;
      },
    });
    for (let i = 0; i < SHAKE_CYCLES; i++) {
      const amp = SHAKE_AMPLITUDE * (1 - i / SHAKE_CYCLES);
      tl.to(scene, { x: amp, duration: SHAKE_CYCLE_DURATION });
      tl.to(scene, { x: -amp, duration: SHAKE_CYCLE_DURATION });
    }

    // Trigger arena releases for both sides. Idempotent on the arena side -
    // the engine's matching arena_release events that fire later will no-op.
    // We hardcode 'cannonball' here because that's the only embeddable object
    // today; when more exist this should query the arena instead.
    const arena = scene.getArena();
    if (arena?.bundle.playArenaEvent) {
      const playCtx = { scene, sheets: arena.sheets, textures: arena.textures };
      for (const side of ['p1', 'p2'] as const satisfies readonly Actor[]) {
        arena.bundle
          .playArenaEvent({ type: 'arena_release', side, objectId: 'cannonball' }, playCtx)
          // Idempotent no-op resolves, so this only catches real drop/explosion
          // failures (gsap throw, missing texture) - surface them, don't swallow.
          .catch((err: unknown) => console.warn(`[earthshake] arena release ${side} failed`, err));
      }
    }

    // Shockwave crack travels along the ground to the opponent. Awaited (unlike
    // the shake / ball-drops above) so playAttack returns AT the moment the
    // crack reaches the defender - the renderer's damage event then fires
    // painAir + damage number exactly on arrival.
    const dir = Math.sign(defender.baseX - attacker.baseX) || 1;
    await travelGroundCrack({
      scene,
      fromX: attacker.baseX + CRACK_START_INSET * dir,
      toX: defender.baseX,
      y: attacker.baseY,
      durationSec: CRACK_TRAVEL_DURATION,
    });
  },
};
